import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, QrCode, FileText, Brain, LogOut, Building2, Wifi } from "lucide-react";

const PAGE_TITLES = {
  "/": "Dashboard",
  "/gestion": "Gestion",
  "/scanner": "Scanner",
  "/rapports": "Rapports",
  "/agent-ia": "Proto IA",
  "/personnalisation": "Personnalisation",
  "/synchro": "Synchronisations",
};

const MOBILE_NAV = [
  { name: "Dashboard", path: "/", icon: LayoutDashboard },
  { name: "Gestion", path: "/gestion", icon: Users },
  { name: "Scanner", path: "/scanner", icon: QrCode },
  { name: "Synchro", path: "/synchro", icon: Wifi },
  { name: "Plus", path: "/personnalisation", icon: Building2 },
];

export default function Layout({ children, onLogout, user }) {
  const location = useLocation();
  const navigation = [
    { name: "Dashboard", path: "/", icon: LayoutDashboard },
    { name: "Gestion", path: "/gestion", icon: Users },
    { name: "Scanner", path: "/scanner", icon: QrCode },
    { name: "Rapports", path: "/rapports", icon: FileText },
    { name: "Synchro", path: "/synchro", icon: Wifi },
    { name: "Proto IA", path: "/agent-ia", icon: Brain },
    { name: "Personnalisation", path: "/personnalisation", icon: Building2 },
  ];
  const pageTitle = PAGE_TITLES[location.pathname] || "ScannerBenj";

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow bg-slate-900 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-6 py-6">
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 rounded-full bg-orange-500 opacity-20 blur-md scale-125"></div>
              <img src="/logo_scanner.png" alt="Logo" className="relative h-11 w-11 rounded-full object-cover ring-2 ring-orange-400 ring-offset-2 ring-offset-slate-900 shadow-lg" />
            </div>
            <h1 className="ml-3 text-xl font-manrope font-bold text-white tracking-tight">ScannerBenj Pro</h1>
          </div>
          <nav className="flex-1 px-3 pb-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link key={item.name} to={item.path}
                  className={"group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors " + (isActive ? "bg-orange-500 text-white shadow-sm" : "text-slate-300 hover:bg-slate-800 hover:text-white")}>
                  <Icon className={"mr-3 h-5 w-5 flex-shrink-0 transition-colors " + (isActive ? "text-white" : "text-slate-400 group-hover:text-white")} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          {/* User info + Logout */}
          <div className="flex-shrink-0 px-4 py-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm">
                {user && user.name ? user.name.charAt(0).toUpperCase() : "?"}
              </div>
              <div className="overflow-hidden">
                <p className="text-white text-sm font-semibold truncate">{user && user.name}</p>
                <p className="text-slate-400 text-xs truncate">{user && (user.role === "admin" ? "Administrateur" : "Régie — " + user.matricule)}</p>
              </div>
            </div>
            <button onClick={onLogout}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-slate-300 hover:text-white hover:bg-red-500/20 rounded-lg transition-colors group">
              <LogOut className="h-4 w-4 text-slate-400 group-hover:text-red-400 transition-colors" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-slate-900 border-b border-slate-700 z-40 h-14 flex items-center px-4">
        <div className="relative flex-shrink-0 mr-3">
          <div className="absolute inset-0 rounded-full bg-orange-500 opacity-20 blur-sm scale-125"></div>
          <img src="/logo_scanner.png" alt="Logo" className="relative h-8 w-8 rounded-full object-cover ring-1 ring-orange-400" />
        </div>
        <span className="text-white font-manrope font-bold text-lg flex-1 truncate">{pageTitle}</span>
        <button onClick={onLogout} className="p-2 text-slate-400 hover:text-red-400 transition-colors">
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      <div className="md:pl-64 flex flex-col flex-1">
        <main className="flex-1 pt-14 md:pt-0 pb-20 md:pb-8">{children}</main>
      </div>

      {/* Mobile bottom nav — 5 items + logout = 6 columns */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 z-50">
        <div className="grid grid-cols-6 h-16">
          {MOBILE_NAV.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path === "/personnalisation" && ["/personnalisation", "/agent-ia"].includes(location.pathname));
            const Icon = item.icon;
            return (
              <Link key={item.name} to={item.path}
                className={"flex flex-col items-center justify-center gap-1 transition-colors " + (isActive ? "text-orange-400" : "text-slate-500 hover:text-slate-300")}>
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{item.name}</span>
              </Link>
            );
          })}
          <button onClick={onLogout} className="flex flex-col items-center justify-center gap-1 text-slate-500 hover:text-red-400 transition-colors">
            <LogOut className="h-5 w-5" />
            <span className="text-xs font-medium">Sortir</span>
          </button>
        </div>
      </nav>
    </div>
  );
}